import { useEffect, useState } from 'react';
import { message } from 'antd';
import useSelectedDeck from './useSelectedDeck';

/**
 * Постраничный список, привязанный к выбранной колоде. Обобщает то, что
 * CardList/QuizList/WordList повторяли друг за другом: состояние страницы,
 * загрузку и защиту от гонок.
 *
 * Сброс страницы при смене колоды происходит прямо в рендере, а не эффектом:
 * эффект загрузки успел бы сходить за страницей от прошлой колоды до того,
 * как сброс применится. Одна пара (колода, страница) — один запрос; ответ
 * отменённого запроса игнорируется, иначе он мог бы перезаписать результат
 * более позднего.
 *
 * fetchPage(deckId, pageIndex, pageSize) должен быть стабильной функцией
 * (модульного уровня) — он входит в зависимости эффекта загрузки.
 */
const usePagedList = (fetchPage, errorMessage, pageSize = 10) => {
    const selectedDeck = useSelectedDeck();
    const deckId = selectedDeck ? selectedDeck.deckId : null;

    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [pagedDeckId, setPagedDeckId] = useState(deckId);

    if (deckId !== pagedDeckId) {
        setPagedDeckId(deckId);
        setCurrentPage(1);
    }

    useEffect(() => {
        if (!deckId) {
            setItems([]);
            setTotal(0);
            return undefined;
        }

        let cancelled = false;
        const fetchItems = async () => {
            setLoading(true);
            try {
                const response = await fetchPage(deckId, currentPage - 1, pageSize);
                if (cancelled) {
                    return;
                }
                setItems(response.content);
                setTotal(response.totalElements);
            } catch (error) {
                if (cancelled) {
                    return;
                }
                console.error('Error fetching page:', error);
                message.error(errorMessage);
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };

        fetchItems();
        return () => {
            cancelled = true;
        };
    }, [deckId, currentPage, fetchPage, errorMessage, pageSize]);

    // Точечное обновление элемента (например, метка на квизе) без перезагрузки страницы
    const updateItem = (updatedItem) => {
        setItems((prevItems) =>
            prevItems.map((item) =>
                item.id === updatedItem.id ? { ...item, ...updatedItem } : item
            )
        );
    };

    // Удаление строки на месте: перезагружать страницу ради одной удалённой карточки
    // незачем, а общий счётчик поправляем, чтобы пагинация не показывала лишнюю страницу
    const removeItem = (itemId) => {
        setItems((prevItems) => prevItems.filter((item) => item.id !== itemId));
        setTotal((prevTotal) => Math.max(prevTotal - 1, 0));
    };

    return {
        selectedDeck, items, loading, total, currentPage, pageSize,
        setPage: setCurrentPage, updateItem, removeItem,
    };
};

export default usePagedList;
